import { AnyAction } from '@reduxjs/toolkit';
import { StateObservable, combineEpics } from 'redux-observable';
import { Observable, distinctUntilChanged, filter, map, switchMap, takeUntil } from 'rxjs';
import { SmoEnvironment } from '../../../../Models/Smo/SmoEnvironment';
import { UserRun } from '../../../../Models/User/UserRun';
import { UserWidgetSettings } from '../../../../Models/User/UserWidgetSettings';
import { mapPayload } from '../../../../Utils/RxjsOperators/MapPayload';
import { SmoSocketClientEvent } from '../../../Socket/Models/SmoSocketClientEvent';
import { SmoSocketServerEvent } from '../../../Socket/Models/SmoSocketServerEvent';
import { StoreDependencies } from '../../Global/StoreDependencies';
import { StoreState } from '../../Global/StoreState';
import WidgetActions from './WidgetActions';

function connectWidget(
  $action: Observable<AnyAction>,
  _$state: StateObservable<StoreState>,
  dependencies: Partial<StoreDependencies>
): Observable<AnyAction> {
  return $action.pipe(
    filter(WidgetActions.connectWidget.match),
    mapPayload(),
    switchMap((sessionId: string) =>
      dependencies.socketManager!.emit$(SmoSocketClientEvent.connect_widget, {
        sessionId: sessionId!
      })
    ),
    switchMap(() => [WidgetActions.observeSettings(), WidgetActions.observeRun(), WidgetActions.observeEnvironment()])
  );
}

function observeSettings(
  $action: Observable<AnyAction>,
  _$state: StateObservable<StoreState>,
  dependencies: Partial<StoreDependencies>
): Observable<AnyAction> {
  return $action.pipe(
    filter(WidgetActions.observeSettings.match),
    switchMap(() =>
      dependencies
        .socketManager!.listen$(SmoSocketServerEvent.settings_updated)
        .pipe(takeUntil($action.pipe(filter(WidgetActions.disconnectWidget.match))))
    ),
    map((response) => response as UserWidgetSettings),
    map(WidgetActions.setSettings)
  );
}

function observeRun(
  $action: Observable<AnyAction>,
  _$state: StateObservable<StoreState>,
  dependencies: Partial<StoreDependencies>
): Observable<AnyAction> {
  return $action.pipe(
    filter(WidgetActions.observeRun.match),
    switchMap(() =>
      dependencies
        .socketManager!.listen$(SmoSocketServerEvent.run_updated)
        .pipe(takeUntil($action.pipe(filter(WidgetActions.disconnectWidget.match))))
    ),
    map((response) => (response as { run: UserRun }).run),
    map(WidgetActions.setRun)
  );
}

function observeEnvironment(
  $action: Observable<AnyAction>,
  _$state: StateObservable<StoreState>,
  dependencies: Partial<StoreDependencies>
): Observable<AnyAction> {
  return $action.pipe(
    filter(WidgetActions.observeEnvironment.match),
    switchMap(() =>
      dependencies
        .socketManager!.listen$(SmoSocketServerEvent.environment_updated)
        .pipe(takeUntil($action.pipe(filter(WidgetActions.disconnectWidget.match))))
    ),
    map((response) => (response as { environment: SmoEnvironment }).environment),
    distinctUntilChanged(),
    map(WidgetActions.setEnvironment)
  );
}

export default combineEpics(connectWidget, observeSettings, observeRun, observeEnvironment);
